import {Attributes, AttributesRow, DataStatus, Table, TableRow} from 'platform/components';
import {Align, Box, Heading, Text, VStack} from 'platform/foundation';

import {useEffect, useState} from 'react';

import {flatten, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useLazyGetMakeModelWithMakeQuery} from '@dms/api/sales';
import {useGetBasicInformationServicePackageQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER} from '@dms/shared';

import {buildArray, suffixTestId, TestIdProps} from 'shared/web';

type Translations = {
  makes: string[] | undefined;
  models: string[] | undefined;
  modelFamilies: string[] | undefined;
};

interface ServicePackageDetailProps extends TestIdProps {
  servicePackageId: string;
}

export function ServicePackageDetail(props: ServicePackageDetailProps) {
  const {data, isError, isLoading} = useGetBasicInformationServicePackageQuery({
    servicePackageId: props.servicePackageId,
  });
  const [getApiMakeModel] = useLazyGetMakeModelWithMakeQuery();

  const [translations, setTranslations] = useState<Translations | null>(null);

  useEffect(() => {
    const vehicleType = data?.servicePackage?.vehicleType;
    const makes = data?.servicePackage?.vehicleMakes;
    const models = data?.servicePackage?.vehicleModels;
    const modelFamilies = data?.servicePackage?.vehicleModelFamilies;

    if (isNil(vehicleType) || isNil(makes)) {
      return;
    }

    Promise.all(
      makes.map((make) =>
        getApiMakeModel({vehicleType, make: make ?? undefined, lang: [i18n.language]})
      )
    ).then((data) => {
      const allModels = flatten(
        flatten(data.map(({data}) => data)).map((data) => data?.models ?? [])
      );

      const makeTranslations = flatten(data.map(({data}) => data)).map(
        (make) => make?.default_label ?? EMPTY_PLACEHOLDER
      );

      const modelTranslations = models?.map(
        (modelName) =>
          allModels.find(({model}) => model === modelName)?.default_label ?? EMPTY_PLACEHOLDER
      );

      const modelFamiliesTranslations = modelFamilies?.map(
        (familyName) =>
          allModels.find((model) => model.model_group?.group === familyName)?.default_label ??
          EMPTY_PLACEHOLDER
      );

      setTranslations({
        makes: makeTranslations,
        models: modelTranslations,
        modelFamilies: modelFamiliesTranslations,
      });
    });
  }, [data, getApiMakeModel]);

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <VStack spacing={4}>
        <Heading size={4}>{i18n.t('general.labels.basicInformation')}</Heading>
        <Box backgroundColor="palettes.neutral.10.100" padding={2} borderRadius="small">
          <Attributes
            rows={buildArray<AttributesRow>()
              .add({label: i18n.t('general.labels.name'), value: data?.servicePackage?.name})
              .add({label: i18n.t('general.labels.number'), value: data?.servicePackage?.number})
              .add({
                label: i18n.t('general.labels.category'),
                value: data?.servicePackage?.servicePackageCategory?.name,
              })
              .add({
                label: i18n.t('entity.workshopServicePackages.labels.competence'),
                value: data?.servicePackage?.competence?.label,
              })
              .add({
                label: i18n.t('entity.workshopServicePackages.labels.estimatedTime'),
                value: data?.servicePackage?.estimatedTime?.toFixed(2).toString(),
              })
              .add({
                label: i18n.t('entity.vehicle.labels.make'),
                value: translations?.makes?.join(', '),
              })
              .add({
                label: i18n.t('entity.vehicle.labels.modelFamily'),
                value: translations?.modelFamilies?.join(', '),
              })
              .add({
                label: i18n.t('entity.vehicle.labels.model'),
                value: translations?.models?.join(', '),
              })
              .add({
                label: i18n.t('general.labels.description'),
                value: data?.servicePackage?.desrciption,
              })}
            size="quarter"
            data-testid={suffixTestId('basicInformation', props)}
          />
        </Box>
        <Heading size={4}>{i18n.t('entity.workshopServicePackages.labels.workItems')}</Heading>
        <DataStatus isEmpty={isNilOrEmpty(data?.servicePackage?.servicePackageItems)}>
          <Table
            data-testid={suffixTestId('table', props)}
            columns={[
              {
                element: (
                  <Box padding={4} backgroundColor="palettes.neutral.10.100">
                    <Align left>
                      <Heading size={6}>{i18n.t('general.labels.type')}</Heading>
                    </Align>
                  </Box>
                ),
              },
              {
                element: (
                  <Box padding={4} backgroundColor="palettes.neutral.10.100">
                    <Align left>
                      <Heading size={6}>{i18n.t('general.labels.number')}</Heading>
                    </Align>
                  </Box>
                ),
              },
              {
                width: '100%',
                element: (
                  <Box padding={4} backgroundColor="palettes.neutral.10.100">
                    <Align left>
                      <Heading size={6}>{i18n.t('general.labels.name')}</Heading>
                    </Align>
                  </Box>
                ),
              },
              {
                element: (
                  <Box padding={4} backgroundColor="palettes.neutral.10.100">
                    <Align right>
                      <Heading size={6}>{i18n.t('general.labels.amount')}</Heading>
                    </Align>
                  </Box>
                ),
              },
            ]}
          >
            {data?.servicePackage?.servicePackageItems?.map((item, index) => (
              <TableRow data-testid={suffixTestId(`tableRow-[${index}]`, props)} key={item?.id}>
                <Box paddingHorizontal={4} paddingVertical={2}>
                  <Text size="small">{item?.type?.label ?? EMPTY_PLACEHOLDER}</Text>
                </Box>
                <Box paddingHorizontal={4} paddingVertical={2}>
                  <Text size="small">{item?.number ?? EMPTY_PLACEHOLDER}</Text>
                </Box>
                <Box paddingHorizontal={4} paddingVertical={2}>
                  <Text size="small">{item?.name ?? EMPTY_PLACEHOLDER}</Text>
                </Box>
                <Box paddingHorizontal={4} paddingVertical={2}>
                  <Text align="right" noWrap>
                    <Text size="small">{`${item?.quantity ?? EMPTY_PLACEHOLDER} ${item?.unit ?? ''}`}</Text>
                  </Text>
                </Box>
              </TableRow>
            ))}
          </Table>
        </DataStatus>
      </VStack>
    </DataStatus>
  );
}
